/** @format */
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ValidatablePlan } from 'common-typescript';
import { Module, StudyPeriodLocator } from 'common-typescript/types';
import { Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocaleService } from 'sis-common';
import { CreditRangePipe, modulesByEducationPhase } from 'sis-components';
import { SelectedValidatablePlanService } from '../service/selected-validatable-plan.service';
import { sortByCuCode } from '../sorting-utils';
import { EducationPhaseStudies, MultiselectGroup, SelectableStudy, StudySelectGroup } from './types';

@Injectable()
export class SelectableStudiesService {
    private readonly creditRangePipe = inject(CreditRangePipe);
    private readonly localeService = inject(LocaleService);
    private readonly selectedValidatablePlanService = inject(SelectedValidatablePlanService);
    private readonly translocoService = inject(TranslocoService);

    readonly untimedStudySelectOptions$ = this.selectedValidatablePlanService.selectedValidatablePlan$.pipe(
        map(validatablePlan => this.createUntimedStudySelectOptions(validatablePlan)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Emits unattained course units and custom study drafts from the currently selected validatable plan.
     * The studies are grouped under education phases (top-level modules), which are further subdivided
     * into groups based on modules. The module structure in the plan is flattened to a single level.
     * Only modules with selectable options are included.
     *
     * Substituted course units are replace by their substitutes, so there may be several {@link SelectableStudy} occurrences for the
     * same course unit, if it substitutes course units in several {@link EducationPhaseStudies}, or several {@link MultiselectGroup}
     * within the same {@link EducationPhaseStudies}.
     */
    readonly educationPhaseStudies$: Observable<EducationPhaseStudies[]> =
        this.selectedValidatablePlanService.selectedValidatablePlan$.pipe(
            map(validatablePlan => this.createEducationPhaseStudies(validatablePlan)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

    private createEducationPhaseStudies(validatablePlan: ValidatablePlan): EducationPhaseStudies[] {
        return (
            validatablePlan
                ?.getSelectedModulesUnderModule(validatablePlan.rootModule)
                .sort(modulesByEducationPhase(validatablePlan.rootModule))
                .map(topLevelModule => ({
                    id: topLevelModule.id,
                    label: this.localeService.localize(topLevelModule.name) ?? '',
                    optionGroups: this.createMultiSelectGroups(validatablePlan, topLevelModule),
                })) ?? []
        );
    }

    private createUntimedStudySelectOptions(validatablePlan: ValidatablePlan) {
        return (
            validatablePlan
                ?.getSelectedModulesUnderModule(validatablePlan.rootModule)
                .sort(modulesByEducationPhase(validatablePlan.rootModule))
                .flatMap(topLevelModule => this.createUntimedStudySelectGroups(validatablePlan, topLevelModule)) ?? []
        );
    }

    private createMultiSelectGroups(validatablePlan: ValidatablePlan, topLevelModule: Module): MultiselectGroup[] {
        // Include the top-level module as well, as it can also contain selectable studies as direct children
        return [topLevelModule, ...getUnattainedModulesSelectedUnderModuleRecursively(validatablePlan, topLevelModule)]
            .map(module => this.createMultiSelectGroup(module, validatablePlan))
            .filter(group => group.options.length > 0)
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    private createUntimedStudySelectGroups(validatablePlan: ValidatablePlan, topLevelModule: Module): StudySelectGroup[] {
        // Include the top-level module as well, as it can also contain selectable studies as direct children
        return [topLevelModule, ...getUnattainedModulesSelectedUnderModuleRecursively(validatablePlan, topLevelModule)]
            .map(module => this.createUntimedStudySelectGroup(module, validatablePlan))
            .filter(group => group.options.length > 0)
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    private createSubLabel(plannedPeriods: StudyPeriodLocator[]): string | undefined {
        if (plannedPeriods?.length === 1) {
            return this.translocoService.translate('TIMELINE.SELECT_COURSE_UNITS.TIMED');
        }
        if (plannedPeriods?.length > 1) {
            return this.translocoService.translate('TIMELINE.SELECT_COURSE_UNITS.TIMED_TO_MULTIPLE_PERIODS');
        }
        return undefined;
    }

    private createMultiSelectGroup(module: Module, validatablePlan: ValidatablePlan): MultiselectGroup {
        return <MultiselectGroup>{
            id: module.id,
            label: this.localeService.localize(module.name) ?? '',
            options: sortByCuCode([
                ...validatablePlan
                    .getCourseUnitsOrSubstitutingCourseUnitsForModule(module)
                    .filter(cu => !validatablePlan.isCourseUnitAttained(cu.id))
                    .map(
                        cu =>
                            <SelectableStudy>{
                                value: {
                                    value: cu,
                                    type: 'CourseUnit' as const,
                                    plannedPeriods: validatablePlan.getPlannedPeriods(cu) ?? [],
                                },
                                label: `${cu.code} ${this.localeService.localize(cu.name)} (${this.creditRangePipe.transform(cu.credits)})`,
                                subLabel: this.createSubLabel(validatablePlan.getPlannedPeriods(cu)),
                            },
                    ),
                ...validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(module.id).map(
                    studyDraft =>
                        <SelectableStudy>{
                            value: {
                                value: studyDraft,
                                type: 'CustomStudyDraft' as const,
                                plannedPeriods: studyDraft.plannedPeriods ?? [],
                            },
                            label: `${studyDraft.name} (${studyDraft.credits} ${this.translocoService.translate('CREDITS')})`,
                            subLabel: this.createSubLabel(studyDraft.plannedPeriods),
                        },
                ),
            ]),
        };
    }

    private createUntimedStudySelectGroup(module: Module, validatablePlan: ValidatablePlan): StudySelectGroup {
        return <StudySelectGroup>{
            id: module.id,
            label: this.localeService.localize(module.name) ?? '',
            options: sortByCuCode([
                ...validatablePlan
                    .getCourseUnitsOrSubstitutingCourseUnitsForModule(module)
                    .filter(cu => !validatablePlan.isCourseUnitAttained(cu.id) && !validatablePlan.isCourseUnitTimed(cu.id))
                    .map(
                        cu =>
                            <SelectableStudy>{
                                value: {
                                    value: cu,
                                    type: 'CourseUnit' as const,
                                    plannedPeriods: validatablePlan.getPlannedPeriods(cu) ?? [],
                                },
                                label: `${cu.code} ${this.localeService.localize(cu.name)} (${this.creditRangePipe.transform(cu.credits)})`,
                            },
                    ),
                ...validatablePlan
                    .getSelectedCustomStudyDraftsByParentModuleId(module.id)
                    .filter(customStudyDraft => !validatablePlan.isCustomStudyDraftTimed(customStudyDraft.id))
                    .map(
                        studyDraft =>
                            <SelectableStudy>{
                                value: {
                                    value: studyDraft,
                                    type: 'CustomStudyDraft' as const,
                                    plannedPeriods: studyDraft.plannedPeriods ?? [],
                                },
                                label: `${studyDraft.name} (${studyDraft.credits} ${this.translocoService.translate('CREDITS')})`,
                            },
                    ),
            ]),
        };
    }
}

function getUnattainedModulesSelectedUnderModuleRecursively(validatablePlan: ValidatablePlan, module: Module): Module[] {
    if (validatablePlan.isModuleAttained(module.id)) {
        return [];
    }

    const childModules = validatablePlan.getSelectedModulesUnderModule(module).filter(child => !validatablePlan.isModuleAttained(child.id));
    return [...childModules, ...childModules.flatMap(child => getUnattainedModulesSelectedUnderModuleRecursively(validatablePlan, child))];
}
